// Versão: 1.01
// Data..: 2026-09-24
// Upload de anexos: pede URL pré-assinada à API e envia o arquivo direto ao S3.
// v1.01: upload público de imagem (uploadPublicImage) para o portal sem login.

import type { Attachment } from '@makweb/shared';
import { api } from './api.js';
import { config } from '../config.js';

// Faz upload de um arquivo e retorna o Attachment pronto para anexar a ticket/mensagem.
export async function uploadFile(file: File): Promise<Attachment> {
  const { uploadUrl, s3Key } = await api.post<{ uploadUrl: string; s3Key: string }>('/uploads', {
    fileName: file.name,
    contentType: file.type || 'application/octet-stream',
  });
  return putToS3(file, uploadUrl, s3Key);
}

// Limite de tamanho para uploads públicos (imagens do corpo do chamado sem login).
const PUBLIC_MAX_BYTES = 5 * 1024 * 1024; // 5 MB

// Upload público (sem login): usado no portal para imagens no corpo do chamado.
// Valida tipo (imagem) e tamanho no cliente antes de pedir a URL pré-assinada.
export async function uploadPublicImage(file: File): Promise<Attachment> {
  if (!file.type.startsWith('image/')) throw new Error('Apenas imagens são permitidas.');
  if (file.size > PUBLIC_MAX_BYTES) throw new Error('Imagem muito grande (máximo 5 MB).');
  const { uploadUrl, s3Key } = await publicRequest(file);
  return putToS3(file, uploadUrl, s3Key);
}

// Pede a URL pré-assinada pública (rota sem autenticação).
async function publicRequest(file: File): Promise<{ uploadUrl: string; s3Key: string }> {
  const res = await fetch(`${config.apiUrl}/public/uploads`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fileName: file.name, contentType: file.type }),
  });
  if (!res.ok) {
    const detail = await res.json().catch(() => ({ message: res.statusText }));
    throw new Error(detail.message ?? 'Falha ao preparar o envio da imagem');
  }
  return res.json() as Promise<{ uploadUrl: string; s3Key: string }>;
}

// Envia o arquivo para o S3 via URL pré-assinada e monta o Attachment.
async function putToS3(file: File, uploadUrl: string, s3Key: string): Promise<Attachment> {
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  });
  if (!res.ok) throw new Error('Falha ao enviar o anexo');
  return {
    id: s3Key,
    fileName: file.name,
    contentType: file.type || 'application/octet-stream',
    size: file.size,
    s3Key,
  };
}
